import React, { useEffect, useMemo, useState } from 'react';
import cs from 'classnames';
import styles from './index.module.less';
import { cloneElement } from './reactNode';

const SpinSizes = ['small', 'default', 'large'] as const;
export type SpinSize = (typeof SpinSizes)[number];

// 是否应该延迟加载
function shouldDelay(spinning?: boolean, delay?: number): boolean {
  return !!spinning && !!delay && !isNaN(Number(delay));
}

// Render indicator
const defaultIndicator: React.ReactNode = null;

function renderIndicator(props: V1SpinProps): React.ReactNode {
  const { indicator } = props;
  const dotClassName = 'V1SpinDot';

  // should not be render default indicator when indicator value is null
  if (indicator === null) {
    return null;
  }

  if (React.isValidElement(indicator)) {
    return cloneElement(indicator, {
      className: cs(indicator.props.className, dotClassName, styles.V1AnticonSpin),
    });
  }

  if (React.isValidElement(defaultIndicator)) {
    return cloneElement(defaultIndicator, {
      className: cs(defaultIndicator.props.className, dotClassName),
    });
  }

  return (
    <span className={cs(styles[dotClassName], styles.V1SpinDotSpin)}>
      <i className={styles.V1SpinDotItem} key={1} />
      <i className={styles.V1SpinDotItem} key={2} />
      <i className={styles.V1SpinDotItem} key={3} />
      <i className={styles.V1SpinDotItem} key={4} />
    </span>
  );
}

export interface V1SpinProps {
  /**
   * @description spin的类属性
   */
  className?: string;
  /**
   * @description 额外插入的外层样式
   */
  style?: React.CSSProperties;
  /**
   * @description 延迟显示加载效果的时间（防止闪烁）,单位 ms
   * @default 0
   */
  delay?: number;
  /**
   * @description 加载指示符
   */
  indicator?: React.ReactNode;
  /**
   * @description 组件大小，可选值为 [small, default, large]
   * @default default
   */
  size?: SpinSize;
  /**
   * @description 是否为加载中状态
   * @default true
   */
  spinning?: boolean;
  /**
   * @description 当作为包裹元素时，可以自定义描述文案
   */
  tip?: React.ReactNode;
  /**
   * @description 包装器的类属性
   */
  wrapperClassName?: string;
  /**
   * @description 显示带有 Spin 组件的背景
   * @default false
   */
  fullscreen?: boolean;
  children?: React.ReactNode;
}

/**
* @description 便捷文档地址
* @see https://reactmobile.lanhanba.net/feedbacks/v1-spin
*/
const V1Spin: React.FC<V1SpinProps> = (props) => {
  const {
    spinning: customSpinning = true,
    delay = 0,
    className,
    size = 'default',
    tip,
    wrapperClassName,
    style,
    children,
    fullscreen,
  } = props;
  const [spinning, setSpinning] = useState<boolean>(
    () => customSpinning && !shouldDelay(customSpinning, delay),
  );
  useEffect(() => {
    if (customSpinning) {
      // 简单实现
      const t = setTimeout(() => {
        setSpinning(true);
      }, delay);
      return () => {
        clearTimeout(t);
      };
    }
    setSpinning(false);
    return;
  }, [delay, customSpinning]);

  // 是否嵌套模式
  const isNestedPattern = useMemo<boolean>(
    () => typeof children !== 'undefined' && !fullscreen,
    [children, fullscreen],
  );

  /* methods */
  const spinElement: React.ReactNode = (
    <div
      style={style}
      className={cs(
        styles.V1Spin,
        {
          [styles.V1SpinSm]: size === 'small',
          [styles.V1SpinLg]: size === 'large',
          [styles.V1SpinSpinning]: spinning,
          [styles.V1SpinShowText]: !!tip,
          [styles.V1SpinFullscreen]: fullscreen,
          [styles.V1SpinFullscreenShow]: fullscreen && spinning,
        },
        className,
      )}
      aria-live='polite'
      aria-busy={spinning}
    >
      {renderIndicator(props)}
      {tip && (isNestedPattern || fullscreen) ? (
        <div className={styles.V1SpinText}>{tip}</div>
      ) : null}
    </div>
  );

  if (isNestedPattern) {
    return (<div className={cs(styles.V1SpinNestedLoading, wrapperClassName)} >
      {spinning && <div key='loading'>{spinElement}</div>}
      <div className={cs(styles.V1SpinContainer, { [styles.V1SpinBlur]: spinning })} key='container'>
        {children}
      </div>
    </div>);
  } else {
    return spinElement;
  }
};

export default V1Spin;
